import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  EMAIL_AI_ERROR_MESSAGE,
  EMAIL_AI_HELPER_TEXT,
  EMAIL_AI_PROMPT_PLACEHOLDER,
  EMAIL_AI_TONE_OPTIONS,
  buildWriteEmailPayload,
  canGenerateEmail,
  canUseGeneratedEmail,
} from '../../utils/writeEmailAIUtils';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const WriteEmailWithAIModal = ({
  isOpen,
  onClose,
  onUseEmail,
  workflowId,
  stepId,
  existingEmail = '',
  availableVariables = [],
  token,
  isPlainText = false,
}) => {
  const [tone, setTone] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const resetState = useCallback(() => {
    setTone('');
    setPrompt('');
    setGeneratedEmail('');
    setIsGenerating(false);
    setError('');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const handleGenerate = async () => {
    if (!canGenerateEmail(prompt)) return;

    setIsGenerating(true);
    setError('');
    setGeneratedEmail('');

    const payload = buildWriteEmailPayload({
      tone: tone || 'Friendly',
      prompt,
      workflowId,
      stepId,
      existingEmail,
      availableVariables,
    });

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/automations/ai/write-email`, payload, { headers });
      const email = response.data?.email || '';
      if (!email.trim()) {
        setError(EMAIL_AI_ERROR_MESSAGE);
        return;
      }
      setGeneratedEmail(email);
    } catch (err) {
      console.error('Write email AI error:', err);
      setError(EMAIL_AI_ERROR_MESSAGE);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseEmail = () => {
    if (!canUseGeneratedEmail(generatedEmail)) return;
    onUseEmail?.(generatedEmail);
    onClose?.();
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent priority className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="write-email-ai-modal">
        <div className="p-6 pb-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-xl font-semibold text-slate-900">
              Write Email with AI
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed">
              Use AI to generate the email. Simply describe what you need, and AI will write the email for you.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ai-email-tone" className="text-sm font-semibold text-slate-800">
                Tone
              </Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger
                  id="ai-email-tone"
                  data-testid="ai-email-tone-select"
                  className="h-11 bg-white border-slate-200 focus:ring-2 focus:ring-blue-500/20"
                >
                  <SelectValue placeholder="Select tone" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 shadow-lg rounded-lg">
                  {EMAIL_AI_TONE_OPTIONS.map((option) => (
                    <SelectItem
                      key={option}
                      value={option}
                      data-testid={`ai-email-tone-${option.toLowerCase()}`}
                      className="cursor-pointer"
                    >
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-email-prompt" className="text-sm font-semibold text-slate-800">
                Type your prompt
              </Label>
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20">
                <Textarea
                  id="ai-email-prompt"
                  data-testid="ai-email-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={EMAIL_AI_PROMPT_PLACEHOLDER}
                  className="min-h-[140px] border-0 rounded-none resize-none shadow-none focus-visible:ring-0 text-sm"
                />
                <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-3 py-2.5 bg-white">
                  <p className="flex items-start gap-1.5 text-xs text-slate-500 leading-snug max-w-[65%]">
                    <Wand2 size={14} className="mt-0.5 shrink-0 text-slate-400" />
                    {EMAIL_AI_HELPER_TEXT}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="ai-email-generate-button"
                    disabled={!canGenerateEmail(prompt) || isGenerating}
                    onClick={handleGenerate}
                    className={cn(
                      'shrink-0 border-purple-500 text-purple-600 hover:bg-purple-50 hover:text-purple-700',
                      'disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent'
                    )}
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 size={14} className="mr-1.5" />
                        Generate email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="ai-email-error">
                {error}
              </p>
            )}

            {generatedEmail && (
              <div className="space-y-2" data-testid="ai-email-preview">
                <Label className="text-sm font-semibold text-slate-800">Generated email</Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 max-h-[220px] overflow-y-auto">
                  {isPlainText ? (
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                      {generatedEmail.replace(/<[^>]+>/g, '')}
                    </pre>
                  ) : (
                    <div
                      className="prose prose-sm max-w-none text-slate-700"
                      dangerouslySetInnerHTML={{ __html: generatedEmail }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 bg-white">
          <Button
            type="button"
            variant="ghost"
            data-testid="ai-email-cancel-button"
            onClick={handleCancel}
            className="text-slate-600"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="ai-email-use-button"
            disabled={!canUseGeneratedEmail(generatedEmail)}
            onClick={handleUseEmail}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
          >
            Use email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WriteEmailWithAIModal;
